import { formatDate } from 'lib/helpers/dateUtils';
import { getImageFileId } from 'lib/helpers/exportPdf/pdfHeader';
import { filestackPath, getInitials, iaRa, removeHtmlTags } from 'lib/helpers/utility';

import { formatLabel } from '../../../../lib/helpers';
import { ACTION_MAP } from '../constants';
import { formatChangedValues } from '../formatChangedValues';

// Helper to clean text for PDF (remove HTML, normalize whitespace)
const cleanText = (text) => {
  if (
    text === null ||
    text === undefined ||
    text === 'undefined' ||
    text === '' ||
    text === 'blank' ||
    text === 'null'
  ) {
    return text;
  }

  let cleaned = removeHtmlTags(String(text), { preserveSpacing: false });
  cleaned = cleaned.replace(/\n/g, ' ');
  cleaned = cleaned.replace(/\s+/g, ' ');
  return cleaned.trim();
};

const getAvatarImagePath = (profilePhoto) => {
  if (!profilePhoto) {
    return;
  }

  const finalImageId = getImageFileId(profilePhoto);

  if (!finalImageId) {
    return;
  }

  const img = filestackPath(finalImageId);

  return img;
};

const getAvatarDetails = (creator, fullName = 'Unknown') => {
  const avatarImage = getAvatarImagePath(creator?.profile_photo);

  return {
    avatarImage,
    avatarInitials: getInitials(fullName || 'Unknown')
  };
};

const formatActivityForPdf = ({ item, isFirstItem, category, timezone = null }) => {
  const { creator, created_at, prefix = '', formattedChanges = [], isNestedCreate, action } = item;
  const fullName = creator ? `${creator.f_name} ${creator.l_name}` : 'Unknown';
  const { avatarImage, avatarInitials } = getAvatarDetails(creator, fullName);
  const time = formatDate(created_at, timezone, 'h:mm a');

  const isCreateRecord = action === 'create' && isFirstItem;

  const actionText = ACTION_MAP[isNestedCreate ? 'create' : action] || 'updated';
  const authorPrefix = `${fullName} ${actionText}${prefix ? ` ${prefix}` : ''}`;
  const authorSuffix = isCreateRecord ? ` this ${category || 'record'}` : ':';

  const changes = [];
  const isDelete = action === 'delete';

  // For record creation, just return the author info and creation text
  if (isCreateRecord) {
    return {
      time,
      changes: [],
      authorText: `${authorPrefix}${authorSuffix}`,
      avatarImage,
      avatarInitials
    };
  }

  formattedChanges.forEach((item) => {
    const { key, isNestedKey, prevValueRaw, currValueRaw } = item;

    const label = formatLabel(key, isNestedKey);

    if (isNestedKey) {
      const formattedResults = formatChangedValues({
        activities: [{ previousValues: prevValueRaw, currentValues: currValueRaw }]
      });
      const formattedChanges = formattedResults[0]?.formattedChanges || [];

      const { changes: nestedChanges } = formatActivityForPdf({ item: { formattedChanges } });

      changes.push({ type: 'update', label, changes: nestedChanges });
      return;
    }

    const prevValue = cleanText(item.prevValue) || 'blank';
    const currValue = cleanText(item.currValue) || 'blank';

    if (prevValue !== currValue) {
      if (isDelete) {
        // For deletes, show what was removed
        changes.push({ type: 'delete', label, prevValue });
      } else {
        // For updates, show from → to
        changes.push({ type: 'update', label, prevValue, currValue });
      }
    }
  });

  return {
    time,
    authorText: `${authorPrefix}${authorSuffix}`,
    changes,
    avatarImage,
    avatarInitials
  };
};

const formatCommentForPdf = (comment, isReply = false, timezone = null) => {
  const { creator, created_at, updated_at, value } = comment;
  const fullName = creator ? `${creator.f_name} ${creator.l_name}` : 'Unknown';
  const { avatarImage, avatarInitials } = getAvatarDetails(creator, fullName);
  const time = formatDate(created_at, timezone, 'h:mm a');
  const commentText = cleanText(value || '');
  const isEdited = updated_at && created_at !== updated_at;

  return {
    time,
    authorText: `${fullName} ${isReply ? 'replied' : 'commented'}:`,
    changes: [commentText ? `${commentText}${isEdited ? ' (edited)' : ''}` : 'Comment: (empty)'],
    isReply,
    avatarImage,
    avatarInitials
  };
};

export const buildFormattedActivities = ({ groupedActivities, category }) => {
  if (!groupedActivities || groupedActivities.dateKeys.length === 0) {
    return [];
  }

  const formattedActivities = [];

  groupedActivities.dateKeys.forEach((dateKey, grIndex) => {
    const activities = groupedActivities.grouped[dateKey];

    formattedActivities.push({ dateKey });

    iaRa(activities).forEach((item, index) => {
      if (item.type === 'comment') {
        const replies = iaRa(item.replies);

        // Add the main comment
        formattedActivities.push({
          ...formatCommentForPdf(item, false),
          isReplyNext: replies.length > 0
        });

        // Add replies if they exist (they share the parent's activity index)
        replies.forEach((reply, replyIndex) => {
          formattedActivities.push({
            ...formatCommentForPdf(reply, true),
            isReplyNext: replyIndex < replies.length - 1
          });
        });
      } else {
        const isFirstItem = index === 0 && grIndex === 0;

        formattedActivities.push(formatActivityForPdf({ item, isFirstItem, category }));
      }
    });
  });

  return formattedActivities;
};
